import React, { useState, useEffect, CSSProperties } from 'react';
import { GoogleMap, Marker, InfoWindow, Circle } from '@react-google-maps/api';

// Interface definitions
interface ExtendedPlaceResult extends google.maps.places.PlaceResult {
  editorial_summary?: { overview?: string };
}

interface POIDetails {
  placeId: string;
  name: string;
  description: string;
  lat: number;
  lng: number;
  photos: string[];
  address: string;
  distanceToHarbor?: number;
}

interface HarborLocation {
  lat: number | string;
  lng: number | string;
  name?: string;
}
export const fetchPlaceDetails = (
  service: google.maps.places.PlacesService | null,
  placeId: string,
  lat: number,
  lng: number,
  callback: (
    name: string,
    description: string,
    photos: string[],
    address: string
  ) => void
) => {
  if (!service) return;

  service.getDetails(
    {
      placeId,
      fields: [
        'name',
        'formatted_address',
        'geometry',
        'photos',
        'editorial_summary',
      ],
    },
    (place, status) => {
      if (
        status === google.maps.places.PlacesServiceStatus.OK &&
        place?.geometry?.location
      ) {
        const extendedPlace = place as ExtendedPlaceResult;
        const photos =
          extendedPlace.photos?.map((photo) =>
            photo.getUrl({ maxWidth: 400, maxHeight: 400 })
          ) || [];
        callback(
          extendedPlace.name || 'Unknown',
          extendedPlace.editorial_summary?.overview ||
            'No description available',
          photos,
          extendedPlace.formatted_address || 'Address not available'
        );
      }
    }
  );
};
// Haversine formula to calculate distance in nautical miles
export const calculateDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number => {
  const R = 3440.065; // Earth's radius in nautical miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const MapComponent: React.FC<{
  onSelectPOI?: (
    placeId: string,
    name: string,
    description: string,
    lat: number,
    lng: number,
    photos: string[],
    address: string
  ) => void;
  selectedPOIs?: POIDetails[];
  style?: CSSProperties;
  enableSearch?: boolean;
  searchTerm?: string;
  onSearchChange?: (searchTerm: string) => void;
  harborLocation?: HarborLocation;
}> = ({ onSelectPOI, selectedPOIs = [], style, harborLocation }) => {
  const [isOpen, setIsOpen] = useState<string | null>(null);
  const [service, setService] =
    useState<google.maps.places.PlacesService | null>(null);

  // Validate and parse coordinates
  const isValidCoordinate = (coord: number | string | undefined): boolean => {
    if (coord === undefined || coord === null) return false;
    const num = typeof coord === 'string' ? parseFloat(coord) : coord;
    return typeof num === 'number' && isFinite(num) && !isNaN(num);
  };

  // Parse harborLocation coordinates
  const parsedHarborLocation = harborLocation
    ? {
        lat:
          typeof harborLocation.lat === 'string'
            ? parseFloat(harborLocation.lat)
            : harborLocation.lat,
        lng:
          typeof harborLocation.lng === 'string'
            ? parseFloat(harborLocation.lng)
            : harborLocation.lng,
        name: harborLocation.name,
      }
    : undefined;

  // Set default center, ensuring valid coordinates
  const defaultCenter: google.maps.LatLngLiteral =
    parsedHarborLocation &&
    isValidCoordinate(parsedHarborLocation.lat) &&
    isValidCoordinate(parsedHarborLocation.lng)
      ? { lat: parsedHarborLocation.lat, lng: parsedHarborLocation.lng }
      : { lat: 63.096, lng: 21.61577 };

  useEffect(() => {
    if (window.google && window.google.maps) {
      setService(
        new google.maps.places.PlacesService(document.createElement('div'))
      );
    }
  }, []);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng || !service || !onSelectPOI || !parsedHarborLocation) return;

    const lat = e.latLng.lat();
    const lng = e.latLng.lng();

    service.nearbySearch(
      {
        location: { lat, lng },
        radius: 50,
        type: 'point_of_interest',
      },
      (results, status) => {
        if (
          status === google.maps.places.PlacesServiceStatus.OK &&
          results &&
          results[0] &&
          results[0].place_id
        ) {
          const place = results[0] as ExtendedPlaceResult;
          const placeLat = place.geometry?.location?.lat() || lat;
          const placeLng = place.geometry?.location?.lng() || lng;
          const placeDistance = calculateDistance(
            parsedHarborLocation.lat,
            parsedHarborLocation.lng,
            placeLat,
            placeLng
          );
          if (placeDistance <= 5000) {
            fetchPlaceDetails(
              service,
              place.place_id!,
              placeLat,
              placeLng,
              (name, description, photos, address) => {
                onSelectPOI(
                  place.place_id!,
                  name,
                  description,
                  placeLat,
                  placeLng,
                  photos,
                  address
                );
                setIsOpen(place.place_id!);
              }
            );
          }
        }
      }
    );
  };

  // Circle options for 5km radius
  const circleOptions = {
    strokeColor: '#FF0000',
    strokeOpacity: 0.8,
    strokeWeight: 2,
    fillColor: '#FF0000',
    fillOpacity: 0.15,
    center:
      parsedHarborLocation &&
      isValidCoordinate(parsedHarborLocation.lat) &&
      isValidCoordinate(parsedHarborLocation.lng)
        ? { lat: parsedHarborLocation.lat, lng: parsedHarborLocation.lng }
        : defaultCenter,
    radius: 5000,
    clickable: false,
  };

  return (
    <div className="relative">
      <GoogleMap
        mapContainerStyle={{ height: '400px', width: '100%', ...style }}
        center={defaultCenter}
        zoom={14}
        onClick={handleMapClick}
      >
        {parsedHarborLocation &&
          isValidCoordinate(parsedHarborLocation.lat) &&
          isValidCoordinate(parsedHarborLocation.lng) && (
            <>
              <Marker
                key="harbor"
                position={{
                  lat: parsedHarborLocation.lat,
                  lng: parsedHarborLocation.lng,
                }}
                title={parsedHarborLocation.name || 'Harbor Location'}
                icon={{
                  url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
                  scaledSize: new google.maps.Size(40, 40),
                }}
              />
              <Circle
                center={{
                  lat: parsedHarborLocation.lat,
                  lng: parsedHarborLocation.lng,
                }}
                options={circleOptions}
              />
            </>
          )}
        {selectedPOIs.map((poi) => (
          <Marker
            key={poi.placeId}
            position={{ lat: poi.lat, lng: poi.lng }}
            title={poi.name}
            onClick={() => setIsOpen(poi.placeId)}
            icon={{
              url: 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png',
              scaledSize: new google.maps.Size(40, 40),
            }}
          />
        ))}
        {selectedPOIs.map(
          (poi) =>
            isOpen === poi.placeId && (
              <InfoWindow
                key={poi.placeId}
                position={{ lat: poi.lat, lng: poi.lng }}
                onCloseClick={() => setIsOpen(null)}
              >
                <div className="max-w-xs">
                  <h3 className="font-medium text-gray-900 dark:text-gray-900 contrast:text-black">
                    {poi.name}
                  </h3>
                  <p>{poi.address}</p>
                  {poi.photos && poi.photos.length > 0 && (
                    <img
                      src={poi.photos[0]}
                      alt={`${poi.name} photo`}
                      className="mt-2 w-full h-32 object-cover rounded"
                    />
                  )}
                  {poi.distanceToHarbor !== undefined && (
                    <p className="text-sm text-gray-500 mt-2 contrast:text-black">
                      Distance to Harbor: {poi.distanceToHarbor.toFixed(2)} nm
                    </p>
                  )}
                  <p className="text-sm text-gray-500 mt-2 contrast:text-black">
                    {poi.description}
                  </p>
                </div>
              </InfoWindow>
            )
        )}
      </GoogleMap>
    </div>
  );
};

export default MapComponent;
